/**
 * « Vidéo » (lot 7) — exporter l'animation en YouTube Short, TikTok,
 * Instagram Reels, depuis l'écran « Tester ».
 *
 *   - contrôle des voix : les parties sans ta voix seront dites par une voix
 *     de synthèse (IA), et c'est dit (générique, description) ;
 *   - la partie « À toi » (questions, boîte à outils : payante) n'est pas dans
 *     la vidéo, elle reste dans l'app ;
 *   - la vidéo montre la VERSION PUBLIÉE : si le brouillon a changé, le dire ;
 *   - sons de CETTE vidéo : musique, son volume, jingles (bibliothèque) ;
 *   - « Créer l'aperçu » : voix de synthèse + document + rendu image par image
 *     par le worker vidéo de ton ordinateur (`animation-video`) — rien n'est
 *     publié ;
 *   - la vidéo rendue se regarde ici, son lien s'ouvre ou se copie (7 jours) ;
 *   - titre / description / légende à relire, puis « Publier » sur les
 *     plateformes choisies (comptes reliés) ; un échec sur une plateforme
 *     (ex. YouTube à reconnecter) se répare et se republie seul.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  ExternalLink,
  Film,
  Loader2,
  RotateCcw,
  Send,
} from 'lucide-react';
import { videoVoiceSlots, type JrafDocument } from '@jeraisonne/animation';
import { cn } from '@jeraisonne/ui';
import { hasUnpublishedChanges, type AnimationRow } from '@/lib/animations/api';
import { useSocialAccount } from '@/lib/admin/youtube-queries';
import { useAnimationMix, useSaveAnimationMix } from '@/lib/animations/audio';
import { soundsSummary, VideoSoundsPicker } from '@/features/animations/animation-video-sounds';
import {
  mixFromVideoSounds,
  videoSoundsFromMix,
  isTikTokInboxDraft,
  publicationErrorMessage,
  soundLabelsOf,
  useAnimationVideoPreview,
  useAnimationVideoStatus,
  useCreateAnimationVideo,
  useLatestAnimationVideo,
  usePublishAnimationVideo,
  useSaveAnimationVideoTexts,
  useRetryAnimationVideo,
  summarizeVideoError,
  videoDuration,
  videoPhasesOf,
  VIDEO_PLATFORMS,
  type AnimationVideoPlatform,
  type VideoSoundLabels,
  type VideoSounds,
} from '@/lib/animations/video';

/** Au-delà, le rendu attend sans doute le worker de ton ordinateur. */
export const WORKER_WAIT_HINT_MS = 45_000;

const STEP_LABEL: Record<string, string> = {
  capturing: 'Rendu des images…',
  composing: 'Voix, musique et montage…',
  thumbnailing: 'Vignette…',
  uploading: 'Dépôt de la vidéo…',
  finalizing: 'Finalisation…',
};

const field =
  'border-ink-200 text-ink-800 focus:border-primary-300 w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none';

export type VideoExportRow = Pick<
  AnimationRow,
  'id' | 'status' | 'slug' | 'published_version' | 'published_at' | 'updated_at'
>;

export function AnimationVideoExport({
  row,
  doc,
  levelLabel,
  subjectLabel,
}: {
  row: VideoExportRow;
  /** brouillon validé (contrôle des voix) */
  doc: JrafDocument | null;
  levelLabel: string | null;
  subjectLabel: string | null;
}) {
  const latest = useLatestAnimationVideo(row.id);
  const create = useCreateAnimationVideo();
  const [platforms, setPlatforms] = useState<AnimationVideoPlatform[]>(['youtube']);
  // les volumes partent du réglage commun (app + vidéos), enregistré au lancement
  const mix = useAnimationMix();
  const saveMix = useSaveAnimationMix();
  const [sounds, setSoundsState] = useState<VideoSounds>(() => videoSoundsFromMix(mix));
  const touched = useRef(false);
  useEffect(() => {
    if (mix !== undefined && !touched.current) setSoundsState(videoSoundsFromMix(mix));
  }, [mix]);
  const setSounds = (next: VideoSounds) => {
    touched.current = true;
    setSoundsState(next);
  };
  const [fresh, setFresh] = useState(false);
  const accounts = {
    youtube: useSocialAccount('youtube'),
    tiktok: useSocialAccount('tiktok'),
    instagram: useSocialAccount('instagram'),
  };
  const connected = (p: AnimationVideoPlatform) =>
    !!accounts[VIDEO_PLATFORMS.find((x) => x.id === p)!.account].data;

  // voix de la VIDÉO : la partie « À toi » (payante) n'y est pas
  const slots = useMemo(() => (doc ? videoVoiceSlots(doc) : []), [doc]);
  const hasPaidPart = !!doc?.scenes.some((s) => s.interactive);
  const missing = slots.filter((s) => !s.audioAssetId);
  const published = !!row.published_version && row.status === 'published';
  const changed = hasUnpublishedChanges(row);

  const video = create.data
    ? {
        id: create.data.video_id,
        title: create.data.title,
        description: create.data.description,
        caption: create.data.caption,
        synthetic: create.data.synthetic,
        durationMs: create.data.duration_ms,
        tooLong: create.data.too_long_for_shorts,
        version: create.data.version_no,
        sounds: create.data.sounds ?? null,
      }
    : latest.data && !fresh
      ? {
          id: latest.data.id,
          title: latest.data.yt_title ?? '',
          description: latest.data.yt_description ?? '',
          caption: latest.data.social_caption ?? '',
          synthetic: latest.data.synthetic ?? [],
          durationMs: latest.data.duration_ms,
          tooLong: (latest.data.duration_ms ?? 0) > 180_000,
          version: latest.data.animation_version,
          sounds: soundLabelsOf(latest.data.soundtrack),
        }
      : null;

  // une version plus récente est en ligne : la dernière vidéo ne la montre pas
  const outdated =
    !!video &&
    !create.data &&
    !fresh &&
    published &&
    video.version !== null &&
    row.published_version !== null &&
    video.version < row.published_version;

  const toggle = (p: AnimationVideoPlatform) =>
    setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  return (
    <div className="space-y-3" data-testid="animation-video-export">
      {!published ? (
        <p className="text-ink-600 text-[13px]">
          Publie d’abord l’animation dans l’app : la vidéo montre la version publiée, et son lien
          mène à sa page.
        </p>
      ) : (
        changed && (
          <p className="bg-warning-50 text-ink-700 rounded-xl px-3 py-2 text-[13px]">
            Le brouillon a changé depuis la version {row.published_version} : la vidéo montre la
            version publiée. Republie d’abord pour y mettre tes dernières modifications et voix.
          </p>
        )
      )}

      {doc && (
        <div className="text-[13px]" data-testid="video-voices">
          {missing.length === 0 ? (
            <p className="text-success-600 font-semibold">Ta voix sur toutes les parties.</p>
          ) : (
            <>
              <p className="text-ink-800 font-semibold">
                {missing.length === slots.length
                  ? 'Pas encore ta voix : toute la vidéo sera dite par une voix de synthèse (IA).'
                  : `Ta voix sur ${slots.length - missing.length} partie${
                      slots.length - missing.length > 1 ? 's' : ''
                    } sur ${slots.length}. ${
                      missing.length > 1 ? 'Les autres seront dites' : 'L’autre sera dite'
                    } par une voix de synthèse (IA) :`}
              </p>
              {missing.length < slots.length && (
                <p className="text-ink-600">{missing.map((s) => s.label).join(' · ')}</p>
              )}
              <p className="text-ink-500 mt-0.5">
                C’est indiqué au générique de la vidéo (« Voix : … et une voix de synthèse ») et
                dans sa description.
              </p>
            </>
          )}
          {hasPaidPart && (
            <p className="text-ink-500 mt-1" data-testid="video-no-paid-part">
              La partie « À toi » (questions, boîte à outils) n’est pas dans la vidéo : elle reste
              dans l’app.
            </p>
          )}
        </div>
      )}

      {outdated && (
        <p className="bg-warning-50 text-ink-700 rounded-xl px-3 py-2 text-[13px]">
          La dernière vidéo montre la version {video!.version} ; la version {row.published_version}{' '}
          est en ligne : crée une nouvelle vidéo.
        </p>
      )}

      {(!video || fresh || outdated) && (
        <>
          <fieldset className="space-y-1.5">
            <legend className="text-ink-600 mb-1 text-xs font-bold">Plateformes</legend>
            {VIDEO_PLATFORMS.map((p) => (
              <label
                key={p.id}
                className={cn(
                  'flex min-h-11 items-center gap-3 rounded-xl border px-3',
                  platforms.includes(p.id)
                    ? 'border-primary-200 bg-primary-50'
                    : 'border-ink-200 bg-white',
                )}
              >
                <input
                  type="checkbox"
                  className="accent-primary-500 h-5 w-5"
                  checked={platforms.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
                <span className="text-ink-800 flex-1 text-sm font-semibold">{p.label}</span>
                {connected(p.id) ? (
                  <span className="text-success-600 inline-flex items-center gap-1 text-xs font-bold">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Compte relié
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                    <AlertCircle className="h-3.5 w-3.5" aria-hidden /> Non relié
                  </span>
                )}
              </label>
            ))}
          </fieldset>
          <VideoSoundsPicker
            value={sounds}
            onChange={setSounds}
            disabled={create.isPending}
            phases={videoPhasesOf(doc)}
          />
          <button
            type="button"
            disabled={!published || platforms.length === 0 || create.isPending}
            onClick={() => {
              setFresh(false);
              saveMix.mutate(mixFromVideoSounds(sounds));
              create.mutate({ animationId: row.id, levelLabel, subjectLabel, platforms, sounds });
            }}
            className="bg-primary-600 hover:bg-primary-700 flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Film className="h-4 w-4" aria-hidden />
            )}
            {create.isPending
              ? missing.length > 0
                ? 'Voix de synthèse et préparation…'
                : 'Préparation…'
              : 'Créer l’aperçu vidéo'}
          </button>
          {create.error && (
            <p role="alert" className="text-error-600 text-[13px]">
              {create.error.message}
            </p>
          )}
        </>
      )}

      {video && !fresh && (
        <VideoReview
          key={video.id}
          animationId={row.id}
          video={video}
          platforms={platforms}
          connected={connected}
          onRedo={() => {
            create.reset();
            setFresh(true);
          }}
        />
      )}
    </div>
  );
}

interface ReviewVideo {
  id: string;
  title: string;
  description: string;
  caption: string;
  synthetic: { key: string; label: string }[];
  durationMs: number | null;
  tooLong: boolean;
  version: number | null;
  sounds: VideoSoundLabels | null;
}

const primaryBtn =
  'bg-primary-600 hover:bg-primary-700 flex min-h-11 items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-white disabled:opacity-50';
const secondaryBtn =
  'text-ink-700 hover:bg-ink-50 border-ink-200 flex min-h-11 items-center gap-1.5 rounded-xl border bg-white px-3 text-sm font-semibold';

function VideoReview({
  animationId,
  video,
  platforms,
  connected,
  onRedo,
}: {
  animationId: string;
  video: ReviewVideo;
  platforms: AnimationVideoPlatform[];
  connected: (p: AnimationVideoPlatform) => boolean;
  onRedo: () => void;
}) {
  const status = useAnimationVideoStatus(video.id);
  const publish = usePublishAnimationVideo();
  const retry = useRetryAnimationVideo();
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [caption, setCaption] = useState(video.caption);
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);
  const saveTexts = useSaveAnimationVideoTexts();
  const savedTexts = useRef({
    title: video.title,
    description: video.description,
    caption: video.caption,
  });
  /** enregistré à la sortie d'un champ, sans publier */
  const persistTexts = () => {
    const next = { title, description, caption };
    const prev = savedTexts.current;
    if (prev.title === title && prev.description === description && prev.caption === caption)
      return;
    savedTexts.current = next;
    saveTexts.mutate({ videoId: video.id, animationId, ...next });
  };
  const [captionCopied, setCaptionCopied] = useState(false);
  const copyCaption = async () => {
    persistTexts();
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true);
    } catch {
      setCaptionCopied(false);
    }
  };
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, []);

  const s = status.data;
  const job = s?.job ?? null;
  const allPubs = s?.publications ?? [];
  // plateformes de CETTE vidéo (choisies à sa création), sinon celles cochées
  const known = VIDEO_PLATFORMS.map((p) => p.id as string);
  const chosen = allPubs.map((p) => p.platform).filter((p) => known.includes(p));
  const targets = (chosen.length > 0 ? chosen : platforms) as AnimationVideoPlatform[];
  const jobActive = job?.status === 'queued' || job?.status === 'processing';
  const publishing =
    jobActive && allPubs.some((p) => p.status === 'queued' || p.status === 'uploading');
  const rendering = jobActive && !publishing;
  // la vidéo rendue : relue à la fin de chaque rendu ou publication
  const preview = useAnimationVideoPreview(
    video.id,
    s && !jobActive ? `${job?.created_at ?? 'aucun'}:${job?.status ?? ''}` : null,
  );
  const master = preview.data ?? null;
  const publishedPubs = allPubs.filter((p) => p.status === 'published');
  const failedPubs = jobActive
    ? []
    : allPubs.filter((p) => p.status === 'failed' && known.includes(p.platform));
  const toPublish = targets.filter((p) => !publishedPubs.some((x) => x.platform === p));
  const renderError =
    !jobActive &&
    !preview.isLoading &&
    job?.status === 'failed' &&
    (failedPubs.length === 0 || !master)
      ? summarizeVideoError(job.error)
      : null;
  const neverRendered = !!s && !job;
  const canPublish = !!master && !jobActive && toPublish.length > 0;
  const waiting =
    job?.status === 'queued' && now - new Date(job.created_at).getTime() > WORKER_WAIT_HINT_MS;
  const missingAccounts = toPublish.filter((p) => !connected(p));
  const label = (p: string) => VIDEO_PLATFORMS.find((x) => x.id === p)?.label ?? p;
  const needsSyntheticMention = video.synthetic.length > 0 && !/synthèse/i.test(description);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(master!);
      setCopied('ok');
    } catch {
      setCopied('failed');
    }
  };

  return (
    <div className="border-ink-200 space-y-3 rounded-2xl border p-3" data-testid="video-review">
      <div className="text-ink-500 space-y-0.5 text-[12px]">
        <p>
          {video.version ? `Version ${video.version}` : 'Vidéo'}
          {video.durationMs ? ` · ${videoDuration(video.durationMs)}` : ''}
          {video.synthetic.length > 0 &&
            ` · voix de synthèse : ${video.synthetic.map((x) => x.label).join(', ')}`}
        </p>
        {video.sounds && <p data-testid="video-sounds-used">{soundsSummary(video.sounds)}</p>}
      </div>
      {video.tooLong && targets.includes('youtube') && (
        <p className="bg-warning-50 text-ink-800 rounded-lg px-2.5 py-1.5 text-[13px] font-semibold">
          Plus de 3 minutes : YouTube ne la comptera pas comme un Short.
        </p>
      )}

      {(rendering || publishing || !s) && (
        <div className="space-y-1" role="status">
          <p className="text-ink-700 flex items-center gap-2 text-sm font-semibold">
            <Loader2 className="text-primary-500 h-4 w-4 animate-spin" aria-hidden />
            {publishing
              ? 'Publication…'
              : job?.status === 'processing'
                ? (STEP_LABEL[job.current_step ?? ''] ?? 'Rendu…')
                : s
                  ? 'En attente du rendu…'
                  : 'Chargement…'}
          </p>
          {waiting && (
            <p className="text-ink-600 text-[13px]">
              Le rendu se fait sur ton ordinateur : lance le worker vidéo (
              <code className="text-[12px]">pnpm --filter @jeraisonne/video-worker work</code>).
            </p>
          )}
        </div>
      )}

      {(renderError || neverRendered) && (
        <div className="space-y-2">
          {renderError && (
            <div role="alert">
              <p className="text-error-600 flex items-center gap-1.5 text-sm font-bold">
                <AlertCircle className="h-4 w-4" aria-hidden /> Le rendu a échoué
              </p>
              <p className="text-error-600 text-[13px]">{renderError.summary}</p>
            </div>
          )}
          <button
            type="button"
            disabled={retry.isPending}
            onClick={() => retry.mutate({ videoId: video.id, platforms: targets })}
            className={primaryBtn}
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
            {retry.isPending ? 'Relance…' : renderError ? 'Réessayer le rendu' : 'Lancer le rendu'}
          </button>
          {retry.error && (
            <p role="alert" className="text-error-600 text-[13px]">
              {retry.error.message}
            </p>
          )}
          {renderError?.detail && (
            <details className="text-ink-500 text-[12px]">
              <summary className="cursor-pointer select-none">Détails techniques</summary>
              <pre className="bg-ink-50 mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg p-2 text-[11px]">
                {renderError.detail.length > 1500
                  ? `…${renderError.detail.slice(-1500)}`
                  : renderError.detail}
              </pre>
            </details>
          )}
        </div>
      )}

      {publishedPubs.length > 0 && (
        <ul className="space-y-1" data-testid="video-published">
          {publishedPubs.map((p) => (
            <li key={p.platform} className="text-success-600 flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {isTikTokInboxDraft(p) ? (
                <span className="text-ink-800" data-testid="tiktok-inbox">
                  Envoyée dans tes brouillons TikTok : ouvre l’app TikTok (notifications ou
                  brouillons) pour la publier.
                </span>
              ) : (
                <>Publiée sur {label(p.platform)}</>
              )}
              {p.external_url && !isTikTokInboxDraft(p) && (
                <a
                  href={p.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 ml-auto inline-flex items-center gap-1 text-xs font-bold"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Voir
                </a>
              )}
            </li>
          ))}
        </ul>
      )}

      {publishedPubs.some(isTikTokInboxDraft) && (
        <div
          className="border-ink-200 space-y-2 rounded-2xl border p-3"
          data-testid="tiktok-caption"
        >
          <p className="text-ink-700 text-[13px]">
            TikTok ne reçoit pas la légende avec un brouillon : copie-la, puis colle-la dans l’app
            TikTok au moment de publier.
          </p>
          <label className="block">
            <span className="text-ink-600 mb-1 block text-xs font-bold">Légende TikTok</span>
            <textarea
              className={cn(field, 'min-h-24 resize-y text-[13px]')}
              value={caption}
              maxLength={2200}
              onChange={(e) => {
                setCaption(e.target.value);
                setCaptionCopied(false);
              }}
              onBlur={persistTexts}
            />
          </label>
          <button type="button" onClick={() => void copyCaption()} className={secondaryBtn}>
            <Copy className="h-4 w-4" aria-hidden />
            {captionCopied ? 'Légende copiée' : 'Copier la légende'}
          </button>
        </div>
      )}

      {failedPubs.length > 0 && (
        <div role="alert" className="space-y-1" data-testid="video-publish-failed">
          <p className="text-error-600 flex items-center gap-1.5 text-sm font-bold">
            <AlertCircle className="h-4 w-4" aria-hidden /> Publication échouée
          </p>
          {failedPubs.map((p) => (
            <p key={p.platform} className="text-error-600 text-[13px]">
              {publicationErrorMessage(p.platform, p.error)}
            </p>
          ))}
          <Link
            to="/tutor/admin/connexions"
            className="text-primary-700 inline-flex min-h-11 items-center gap-1 text-[13px] font-semibold underline"
          >
            Ouvrir les Paramètres de connexion
          </Link>
        </div>
      )}

      {master && !jobActive && (
        <div className="space-y-2" data-testid="video-preview">
          <video
            src={master}
            controls
            playsInline
            preload="metadata"
            aria-label="Aperçu de la vidéo"
            className="mx-auto aspect-[9/16] w-full max-w-[260px] rounded-xl bg-black"
          />
          <div className="flex flex-wrap items-center justify-center gap-2">
            <a href={master} target="_blank" rel="noreferrer" className={secondaryBtn}>
              <ExternalLink className="h-4 w-4" aria-hidden /> Ouvrir la vidéo
            </a>
            <button type="button" onClick={() => void copyLink()} className={secondaryBtn}>
              <Copy className="h-4 w-4" aria-hidden />
              {copied === 'ok' ? 'Lien copié' : 'Copier le lien'}
            </button>
          </div>
          <p className="text-ink-500 text-center text-[12px]">
            Lien privé, valable 7 jours ; la vidéo n’est pas publiée.
          </p>
          {copied === 'failed' && (
            <input
              readOnly
              aria-label="Lien de la vidéo"
              value={master}
              onFocus={(e) => e.currentTarget.select()}
              className={cn(field, 'text-[12px]')}
            />
          )}
        </div>
      )}

      {canPublish && (
        <div className="space-y-2">
          <label className="block">
            <span className="text-ink-600 mb-1 block text-xs font-bold">Titre (YouTube)</span>
            <input
              className={field}
              value={title}
              maxLength={100}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={persistTexts}
            />
          </label>
          <label className="block">
            <span className="text-ink-600 mb-1 block text-xs font-bold">Description (YouTube)</span>
            <textarea
              className={cn(field, 'min-h-32 resize-y text-[13px]')}
              value={description}
              maxLength={5000}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={persistTexts}
            />
          </label>
          <label className="block">
            <span className="text-ink-600 mb-1 block text-xs font-bold">
              Légende (TikTok, Reels)
            </span>
            <textarea
              className={cn(field, 'min-h-24 resize-y text-[13px]')}
              value={caption}
              maxLength={2200}
              onChange={(e) => setCaption(e.target.value)}
              onBlur={persistTexts}
            />
          </label>
          {saveTexts.isSuccess && (
            <p role="status" className="text-ink-500 text-[12px]">
              Textes enregistrés.
            </p>
          )}
          {saveTexts.error && (
            <p role="alert" className="text-error-600 text-[13px]">
              {saveTexts.error.message}
            </p>
          )}
          {needsSyntheticMention && (
            <p className="text-error-600 text-[13px] font-semibold">
              La description doit dire qu’une voix de synthèse parle.
            </p>
          )}
          {missingAccounts.length > 0 && (
            <p className="text-ink-600 text-[13px]">
              Relie d’abord : {missingAccounts.map(label).join(', ')} (
              <Link
                to="/tutor/admin/connexions"
                className="text-primary-700 font-semibold underline"
              >
                Paramètres de connexion
              </Link>
              ).
            </p>
          )}
          <button
            type="button"
            disabled={
              missingAccounts.length > 0 ||
              publish.isPending ||
              !title.trim() ||
              needsSyntheticMention
            }
            onClick={() =>
              publish.mutate({
                videoId: video.id,
                platforms: toPublish,
                title,
                description,
                caption,
              })
            }
            className={primaryBtn}
          >
            <Send className="h-4 w-4" aria-hidden />
            {publish.isPending
              ? 'Publication…'
              : `${failedPubs.length > 0 ? 'Republier' : 'Publier'} (${toPublish.map(label).join(', ')})`}
          </button>
          {publish.error && (
            <p role="alert" className="text-error-600 text-[13px]">
              {publish.error.message}
            </p>
          )}
        </div>
      )}

      {!jobActive && !!s && (
        <div className="space-y-1">
          <button type="button" onClick={onRedo} className={secondaryBtn}>
            <Film className="h-4 w-4" aria-hidden /> Nouvelle vidéo
          </button>
          <p className="text-ink-500 text-[12px]">
            Autres sons, autres plateformes ou dernière version publiée : les voix de synthèse déjà
            faites sont réutilisées.
          </p>
        </div>
      )}
    </div>
  );
}
